import {
  DocumentSnapshot,
  QueryDocumentSnapshot,
  Timestamp,
  Transaction,
  Unsubscribe,
  deleteDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  where,
} from "firebase/firestore";

import { FirestoreRefs } from "igfd-commons/src/firebase/refs";

import {
  FirestoreMenuDocument,
  FirestoreMenuSection,
  FirestoreRestaurantMenuDocument,
  Menu,
  MenuSection,
  RestaurantMenu,
} from "../../types/menu";
import { removeDishFromSection } from "../../utils/menuSectionTree";
import { FetchMenusResult } from "../reducers/menuReducer";

export interface MenuApi {
  subscribeToMenus(
    restaurantUuid: string,
    onChange: (result: FetchMenusResult) => void,
    onError: (error: Error) => void
  ): Unsubscribe;

  // Sets a menu, optionally allowing the caller to set the menu sections as well.
  // Menu sections are optional because they are not always needed when modifying an attribute of a menu.
  setMenu(
    menu: Menu,
    creatingNew: boolean,
    previousMenuIdsInDisplayOrder: string[],
    menuSectionsOfMenu?: MenuSection[]
  ): Promise<void>;
  deleteMenu(
    restaurantUuid: string,
    menuId: string,
    previousMenuIdsInDisplayOrder: string[],
    menuSectionIdsToDelete: string[]
  ): Promise<void>;

  setMenuSection(menuSection: MenuSection): Promise<void>;
  deleteMenuSection(
    restaurantUuid: string,
    menuId: string,
    menuSectionId: string
  ): Promise<void>;

  setMenuDisplayOrder(
    restaurantUuid: string,
    menuIdsInDisplayOrder: string[]
  ): Promise<void>;
}

const readMenu = (doc: QueryDocumentSnapshot): Menu => {
  const data = doc.data() as FirestoreMenuDocument;
  // drop the fields that are not part of the Menu type
  const {
    restaurantRef,
    menuSectionRefsInDisplayOrder,
    createdAt,
    modifiedAt,
    schedule,
    ...menu
  } = data;
  return {
    ...menu,
    schedule: schedule ?? null,
    createdAtMillis: createdAt ? createdAt.toMillis() : null,
    modifiedAtMillis: modifiedAt ? modifiedAt.toMillis() : null,
  };
};

const readMenuSection = (doc: QueryDocumentSnapshot): MenuSection => {
  const data = doc.data() as FirestoreMenuSection;
  // drop the fields that are not part of the MenuSection type
  const { restaurantRef, menuRef, dishRefsInDisplayOrder, ...menuSection } =
    data;
  return menuSection as MenuSection;
};

const readRestaurantMenu = (doc: DocumentSnapshot): RestaurantMenu => {
  if (!doc.exists()) {
    // No menus yet.
    return { menuIdsInDisplayOrder: [] } as RestaurantMenu;
  }
  const data = doc.data() as FirestoreRestaurantMenuDocument;
  // drop the fields that are not part of the RestaurantMenu type
  const { menuRefsInDisplayOrder, ...restaurantMenu } = data;
  return restaurantMenu as RestaurantMenu;
};

/**
 * An implementation of the MenuApi that uses Firestore as the backend.
 */
export class FirestoreBackedMenuApi implements MenuApi {
  private firestoreRefs: FirestoreRefs;

  constructor(firestoreRefs: FirestoreRefs) {
    this.firestoreRefs = firestoreRefs;
  }

  /**
   * Menus, menu sections and the display order live in three places, so the
   * combined result is only emitted once all three have reported, and again
   * whenever any of them changes.
   */
  public subscribeToMenus(
    restaurantUuid: string,
    onChange: (result: FetchMenusResult) => void,
    onError: (error: Error) => void
  ): Unsubscribe {
    let menus: Menu[] | undefined;
    let menuSections: MenuSection[] | undefined;
    let restaurantMenu: RestaurantMenu | undefined;
    const emit = () => {
      if (menus && menuSections && restaurantMenu) {
        onChange({
          menus,
          menuSections,
          menuIdsInDisplayOrder: restaurantMenu.menuIdsInDisplayOrder,
        });
      }
    };

    const unsubscribes = [
      onSnapshot(
        this.firestoreRefs.menus(restaurantUuid),
        (snapshot) => {
          menus = snapshot.docs.map(readMenu);
          emit();
        },
        onError
      ),
      onSnapshot(
        this.firestoreRefs.menuSections(restaurantUuid),
        (snapshot) => {
          menuSections = snapshot.docs.map(readMenuSection);
          emit();
        },
        onError
      ),
      onSnapshot(
        this.firestoreRefs.restaurantMenu(restaurantUuid),
        (snapshot) => {
          restaurantMenu = readRestaurantMenu(snapshot);
          emit();
        },
        onError
      ),
    ];
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }

  public async setMenu(
    menu: Menu,
    creatingNew: boolean,
    _previousMenuIdsInDisplayOrder: string[],
    menuSections?: MenuSection[]
  ): Promise<void> {
    const { createdAtMillis, modifiedAtMillis, schedule, ...menuRest } = menu;
    const firestoreMenu = {
      ...menuRest,
      schedule: schedule ?? null,
      createdAt: creatingNew
        ? Timestamp.now()
        : createdAtMillis
        ? Timestamp.fromMillis(createdAtMillis)
        : null,
      modifiedAt: Timestamp.now(),
    } as FirestoreMenuDocument;

    // If menu sections are provided, set them as well
    // but if they are not provided, do not touch the menu sections
    // FIXME - At this point if some menu sections were deleted, they will still be in the menu
    // this happens when the menu sections are deleted from the EditMenuDrawer.
    // The menuSection documents remain, but references are deleted from the menu document.
    const menuSectionPromises = menuSections
      ? menuSections.map((menuSection) => this.writeMenuSection(menuSection))
      : [];

    // Prepare every section before making a new menu available. Keeping these
    // writes outside the transaction also supports menus with many sections.
    await Promise.all(menuSectionPromises);

    const menuRef = this.firestoreRefs.menu(menu.restaurantUuid, menu.id);
    if (creatingNew) {
      const restaurantMenuRef = this.firestoreRefs.restaurantMenu(
        menu.restaurantUuid
      );
      return runTransaction(
        this.firestoreRefs.firestore,
        async (transaction) => {
          const restaurantMenu = readRestaurantMenu(
            await transaction.get(restaurantMenuRef)
          );
          // Read the order in the transaction so another creation or reorder
          // cannot be overwritten by the caller's older Redux snapshot.
          transaction.set(menuRef, firestoreMenu, { merge: true });
          transaction.set(
            restaurantMenuRef,
            {
              menuIdsInDisplayOrder: [
                menu.id,
                ...restaurantMenu.menuIdsInDisplayOrder.filter(
                  (menuId) => menuId !== menu.id
                ),
              ],
            },
            { merge: true }
          );
        }
      );
    }

    return setDoc(menuRef, firestoreMenu, { merge: true });
  }

  public async deleteMenu(
    restaurantUuid: string,
    menuId: string,
    previousMenuIdsInDisplayOrder: string[],
    menuSectionIdsToDelete: string[]
  ): Promise<void> {
    // remove the menu from the restaurant menu
    const newMenuIdsInDisplayOrder = previousMenuIdsInDisplayOrder.filter(
      (id) => id !== menuId
    );
    const updateRestaurantMenu = this.setMenuDisplayOrder(
      restaurantUuid,
      newMenuIdsInDisplayOrder
    );

    // remove the menuSecions
    const deleteMenuSections = await Promise.all(
      menuSectionIdsToDelete.map((menuSectionId) =>
        deleteDoc(this.firestoreRefs.menuSection(restaurantUuid, menuSectionId))
      )
    );

    // delete the menu
    const deleteMenu = await deleteDoc(
      this.firestoreRefs.menu(restaurantUuid, menuId)
    );
  }

  public setMenuSection(menuSection: MenuSection): Promise<void> {
    return this.writeMenuSection(menuSection).then(() => {
      this.touchMenu(menuSection.restaurantUuid, menuSection.menuId);
    });
  }

  private writeMenuSection(menuSection: MenuSection): Promise<void> {
    return setDoc(
      this.firestoreRefs.menuSection(
        menuSection.restaurantUuid,
        menuSection.id
      ),
      menuSection,
      {
        // Replace each owned top-level field. `merge: true` recursively
        // preserves removed header ids inside headersById, which leaves
        // orphaned groups after a group is deleted.
        mergeFields: Object.keys(menuSection),
      }
    );
  }

  /**
   * Removes the given dish id from all menu sections that contain it.
   * in the given firestore transaction.
   *
   * in the given transaction, read all MenuSections that contain this dish id.
   * then update all menusections removing this dish id.
   * deleting the dish itself is not done here, and is left to the caller.
   *
   * @param transaction
   * @param restaurantUuid
   * @param dishId
   * @returns
   */
  public deleteDishFromMenuSections(
    transaction: Transaction,
    restaurantUuid: string,
    dishId: string
  ): Promise<void> {
    // FIXME - getDocs isn't in transaction scope here, but it should be.
    return getDocs(
      query(
        this.firestoreRefs.menuSections(restaurantUuid),
        where("dishIdsInDisplayOrder", "array-contains", dishId)
      )
    ).then((querySnapshot) => {
      querySnapshot.docs.forEach((doc) => {
        const data = doc.data() as FirestoreMenuSection;
        const { menuId } = data;
        transaction.update(doc.ref, removeDishFromSection(data, [dishId]));

        this.touchMenu(restaurantUuid, menuId, transaction);
      });
    });
  }

  deleteMenuSection(
    restaurantUuid: string,
    menuId: string,
    menuSectionId: string
  ): Promise<void> {
    // FIXME. this would be moved to the backend api, once we have auth ready.

    // update menu and remove the menuSectionId
    // Use a firestore transaction to read the MenuDoc,
    // remove the given menuSectionId. write the updated menuDoc, and
    // then delete the menuSectionDoc.

    return runTransaction(
      this.firestoreRefs.firestore,
      async (transaction: Transaction) => {
        const menuDocRef = this.firestoreRefs.menu(restaurantUuid, menuId);
        const menuDoc = await transaction.get(menuDocRef);
        if (!menuDoc.exists()) {
          throw new Error("Menu does not exist");
        }
        const menuData = menuDoc.data() as FirestoreMenuDocument;
        transaction.update(menuDocRef, {
          menuSectionIdsInDisplayOrder:
            menuData.menuSectionIdsInDisplayOrder.filter(
              (id) => id !== menuSectionId
            ),
        });

        const menuSectionDocRef = this.firestoreRefs.menuSection(
          restaurantUuid,
          menuSectionId
        );

        transaction.delete(menuSectionDocRef);
        this.touchMenu(restaurantUuid, menuId, transaction);
      }
    );
  }

  setMenuDisplayOrder(
    restaurantUuid: string,
    menuIdsInDisplayOrder: string[]
  ): Promise<void> {
    const firestoreRestaurantMenuDoc: FirestoreRestaurantMenuDocument = {
      menuIdsInDisplayOrder: menuIdsInDisplayOrder,
    };

    return setDoc(
      this.firestoreRefs.restaurantMenu(restaurantUuid),
      firestoreRestaurantMenuDoc,
      {
        merge: true,
      }
    );
  }

  private touchMenu(
    restaurantUuid: string,
    menuId: string,
    transaction?: Transaction
  ): Promise<void> | Transaction {
    if (transaction) {
      return transaction.update(
        this.firestoreRefs.menu(restaurantUuid, menuId),
        {
          modifiedAt: Timestamp.now(),
        }
      );
    }
    return setDoc(
      this.firestoreRefs.menu(restaurantUuid, menuId),
      {
        modifiedAt: Timestamp.now(),
      },
      {
        merge: true,
      }
    );
  }
}
